import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { createPortal } from 'react-dom';
import styled, { withTheme } from 'styled-components';
import ReportBreadcrumb from './ReportBreadcrumb';
import generateCrumbs from '../../utils/reports/generateCrumbs';
import calculatePercentage from '../../utils/reports/calculatePercentage';
import isUserActive from '../../utils/reports/isUserActive';
import getStoreName from '../../utils/reports/getStoreName';
import __ from '../../utils/translator';
import AccordionPrefix from './AccordionPrefix';
import mapObject from '../../utils/reports/mapObject';
import groupBy from 'lodash.groupby';
import Accordion from './Accordion';
import AccordionTitle from './AccordionTitle';
import generateReportUrl from '../../utils/reports/generateReportUrl';
import ModalFullscreenActions from '../../flux/ModalFullscreenActions';
import FeedbackHelpButton from '../FeedbackHelpButton';
import AccordionItemContainer from './AccordionItemContainer';
import AccordionItem from './AccordionItem';
import formatUsername from '../../utils/reports/formatUsername';
import Checkbox from '../Checkbox';
import ButtonFloating from '../ButtonFloating';
import Button from '../Button';
import Danger from '../../icons/Danger';
import Modal from '../Modal';
import RenderOnTablet from "../responsive/RenderOnTablet";
import themeResponsiveProperty from "../responsive/themeResponsiveProperty";
import RenderOnMobile from "../responsive/RenderOnMobile";
import { default as MinusIcon } from "../../icons/Minus";
import { default as PlusIcon } from "../../icons/Plus";
import { ItemSmallLabel, TitleSmallLabel } from "./ReportsLabels";
import NoResults from "../NoResults";
import { default as BaloonIcon } from "../../icons/Baloon";
import ArrowRight from '../../icons/ArrowRight';
import { Link } from 'react-router-dom';

const STORE_CHANNEL_OUTLET = 'FG';

/**
 * Sort by percentage, whether or not the user is active and by name.
 * This is going to give the following precedence while sorting:
 * 1. The percentage: the higher percentage will be on top;
 * 2. Active or not: all the inactive users will be on bottom, and active users
 *    on top;
 * 3. By name: users with the same percentage and status are going to be sorted
 *    alphabetically.
 * @param storeData
 * @returns {*}
 */
function sortStoreData(storeData) {
  return storeData.sort((user1, user2) => {
    const isUser1Active = isUserActive(user1);
    const isUser2Active = isUserActive(user2);

    if (isUser1Active !== isUser2Active) {
      return Number(isUser2Active) - Number(isUser1Active);
    }

    const user1Percentage = calculatePercentage([user1]);
    const user2Percentage = calculatePercentage([user2]);

    if (user1Percentage !== user2Percentage) {
      return user2Percentage - user1Percentage;
    }

    return formatUsername(user1).localeCompare(formatUsername(user2));
  });
}

export default withTheme(({ match, history, isLoading, data }) => {
  const retail = data.filter(row => row.store_channel !== STORE_CHANNEL_OUTLET);
  const outlets = data.filter(row => row.store_channel === STORE_CHANNEL_OUTLET);

  return (
    <div>
      <RenderOnTablet>
        <ReportBreadcrumb crumbs={generateCrumbs(match.params, __, getStoreName(data, match.params))} />

        <AccordionPrefix columns={['50%', '20%', '20%', '10%']}>
          <span>{__('Name')}</span>
          <span>{__('Active users')}</span>
          <span>{__('Content done')}</span>
          <span/>
          <span/>
        </AccordionPrefix>
      </RenderOnTablet>

      {mapObject(groupBy(retail, 'store_id'), (storeData, store) => (
        <StoreAccordion key={store} match={match} history={history} storeData={storeData} store={store}/>
      ))}

      {/*
        * We do not show the "Outlets" header if there are no retail stores (and
        * thus the heading becomes redundant) or if there are no outlets
        */
      retail.length > 0 && outlets.length > 0 && (
        <AccordionPrefix style={{ marginTop: '1.5rem' }}>
          <strong>Outlets</strong>
        </AccordionPrefix>
      )}

      {mapObject(groupBy(outlets, 'store_id'), (storeData, store) => (
        <StoreAccordion key={store} match={match} storeData={storeData} store={store}/>
      ))}

      {isLoading === false && data.length === 0 && (
        <NoResults message={match.params.store !== undefined ? (
          __('No results for the selected store.')
        ) : (
          __('No results for the selected country.')
        )} />
      )}
    </div>
  );
});

const StoreAccordion = withTheme(({
  match,
  history,
  storeData,
  store,
  ...props
}) => {
  const [userSelection, setUserSelection] = useState([]);
  const [isWarnModalOpen, setIsWarnModalOpen] = useState(null);

  // We want to reset the selection when the opened store changes
  if (match.params.store !== store && userSelection.length > 0) {
    setUserSelection([]);
    return null;
  }

  function handleReportProblemButtonClick() {
    ModalFullscreenActions.reportProblem({
      saBoutique: userSelection.length > 1 ? null : userSelection[0].store_thron_name,
      saUsername: userSelection.map(user => formatUsername(user)).join(', '),
    });

    setUserSelection([]);
  }

  function handleUserSelectClick(event, user) {
    const checked = event.target.checked;
    setUserSelection(selection => {
      // We make sure the component state is in sync with the checkbox state
      // by removing the user before taking any action based on the checkbox
      // state.
      const nextSelection = selection.filter(_user => _user.username !== user.username);

      if (checked) {
        return nextSelection.concat([user]);
      } else {
        return nextSelection;
      }
    });
  }

  return (
    <Fragment>
      <Accordion>
        <div style={{ boxShadow: themeResponsiveProperty('0px 0px 4px rgba(0, 0, 0, 0.25)', null)(props) }}>
          {match.params.store === store && createPortal((
            <React.Fragment>
              <RenderOnMobile>
                <Link
                  to={`${generateReportUrl(match.params.collectionType, match.params)}/report-problem?usernames=${userSelection.map(user => user.username).join(',')}`}
                  onClick={() => setUserSelection([])}
                >
                  <ReportProblemButton isVisible={userSelection.length > 0}>
                    {userSelection.length === 1 ? (
                      __('Report :count person', { count: userSelection.length })
                    ) : (
                      __('Report :count people', { count: userSelection.length })
                    )}
                    <ArrowRight style={{ width: '.625rem', marginLeft: '1rem' }} />
                  </ReportProblemButton>
                </Link>
              </RenderOnMobile>

              <RenderOnTablet>
                <ReportProblemButton isVisible={userSelection.length > 0} onClick={handleReportProblemButtonClick}>
                  {__('Report a problem')} ({userSelection.length})
                </ReportProblemButton>
              </RenderOnTablet>
            </React.Fragment>
          ), window.document.body)}

          <AccordionTitle
            to={generateReportUrl(match.params.collectionType, {
              ...match.params,
              store: match.params.store === store ? undefined : store
            })}
            isExpanded={match.params.store === store}
            columns={themeResponsiveProperty(['60%', '10%', '15%', '5%'], ['50%', '20%', '20%', '10%'])(props)}
            replace
          >
            <RenderOnMobile>
              <div style={{display: 'flex', flexDirection: 'column', justifyContent: 'center'}}>
                <TitleSmallLabel>
                  {storeData[0].store_name}
                </TitleSmallLabel>
                <TitleSmallLabel style={{textTransform: 'capitalize', fontWeight: 'normal', fontSize: '.8rem'}}>
                  {__('Active Users')} {storeData.filter(user => isUserActive(user)).length}/{storeData.length}
                </TitleSmallLabel>
              </div>

              <Link to={`${generateReportUrl(match.params.collectionType, match.params)}/suggestion`}>
                <Baloon isVisible={match.params.store === store}>
                  <BaloonIcon style={{ height: '1.25rem' }} />
                </Baloon>
              </Link>
            </RenderOnMobile>

            <RenderOnTablet>
              <span style={{ fontSize: '1.125rem' }}>
                {storeData[0].store_name}
              </span>
              <span style={{ fontSize: '1.5rem' }}>
                <span style={{ color: '#000' }}>
                  {storeData.filter(user => isUserActive(user)).length}
                </span>/{storeData.length}
              </span>
            </RenderOnTablet>

            <TitleSmallLabel>
              {calculatePercentage(storeData)}%
            </TitleSmallLabel>

            <RenderOnMobile>
              <span style={{display: 'flex', alignItems: 'center', justifyContent: 'flex-end'}}>
                {match.params.store === store ? (
                  <MinusIcon style={{ width: '1rem' }} />
                ) : (
                  <PlusIcon style={{ width: '1rem' }} />
                )}
              </span>
            </RenderOnMobile>

            <RenderOnTablet>
              <FeedbackHelpButton onClick={ModalFullscreenActions.suggestion.bind(null, {
                // @todo 2018-10-09
                // Consider using store_thron_name instead, which should
                // be better for debugging but uglier to see
                boutique: storeData[0].store_name,
              })}>
                {__('Suggestion')}
              </FeedbackHelpButton>
            </RenderOnTablet>
          </AccordionTitle>

          <AccordionItemContainer isVisible={match.params.store === store}>
            {sortStoreData(storeData).map(user => (
              <AccordionItem
                key={user.username}
                to={generateReportUrl(match.params.collectionType, {
                  ...match.params,
                  username: user.username,
                })}
                columns={themeResponsiveProperty(['68%', '18%', '12%'], ['50%', '20%', '15%', '15%'])(props)}
                isSelected={userSelection.some(_user => _user.username === user.username)}
                style={{ borderBottom: 0 }}
              >
                <RenderOnMobile>
                  <div style={{display: 'flex', alignItems: 'center'}}>
                    {!isUserActive(user) && (
                      <Checkbox.Wrapper onClick={event => event.stopPropagation()} style={{ padding: '.5rem .75rem .5rem 0' }}>
                        <input type="checkbox"
                          onChange={event => handleUserSelectClick(event, user)}
                          checked={userSelection.some(_user => _user.username === user.username)} />
                        <Checkbox.Check />
                      </Checkbox.Wrapper>
                    )}
                    <ItemSmallLabel style={{ color: isUserActive(user) ? 'auto' : '#FF0000' }}>
                      {formatUsername(user)}
                    </ItemSmallLabel>
                  </div>
                </RenderOnMobile>

                <RenderOnTablet>
                  <ItemSmallLabel>
                    {formatUsername(user)}
                  </ItemSmallLabel>
                  <span style={{
                    fontSize: '1.125rem',
                    paddingTop: 0,
                    paddingBottom: 0,
                  }}>
                    {isUserActive(user) ? null : (
                      <Button disabled link style={{ fontSize: 0 }} onClick={event => {
                        event.preventDefault();
                        setIsWarnModalOpen(true);
                      }}>
                        <Danger style={{ height: '1.5rem' }} />
                      </Button>
                    )}
                  </span>
                </RenderOnTablet>

                <ItemSmallLabel style={{
                  fontWeight: 'bold',
                  textAlign: 'right',
                  color: isUserActive(user) ? null : '#FF0000',
                }}>
                  {calculatePercentage([user])}%
                </ItemSmallLabel>

                <RenderOnTablet>
                  <span style={{textAlign: 'right', paddingTop: 0, paddingBottom: 0}}>
                    {isUserActive(user) === false && (
                      <Checkbox.Wrapper onClick={event => event.stopPropagation()} style={{ padding: '.875rem 1rem' }}>
                        <input type="checkbox"
                          onChange={event => handleUserSelectClick(event, user)}
                          checked={userSelection.some(_user => _user.username === user.username)} />
                        <Checkbox.Check/>
                      </Checkbox.Wrapper>
                    )}
                  </span>
                </RenderOnTablet>

              </AccordionItem>
            ))}
          </AccordionItemContainer>

          {isWarnModalOpen && ReactDOM.createPortal(
            <Modal>
              <Modal.Title>
                {__('What does this icon mean?')}
              </Modal.Title>
              <Modal.Body>
                <p>{__('The "warning" icon, besides a user, means that the user hasn\'t done any activity for the selected Seasonal or Timeless collection.')}</p>
                <p>{__('For Seasonal collections only the activities done within the collection\'s active dates are taken into consideration.')}</p>
              </Modal.Body>
              <Modal.Actions>
                <Button onClick={() => setIsWarnModalOpen(false)}>
                  {__('Ok')}
                </Button>
              </Modal.Actions>
            </Modal>,
            window.document.body
          )}
        </div>
      </Accordion>
    </Fragment>
  );
});

const Baloon = styled.span`
  transition: transform 300ms ease,
              opacity 300ms ease;
  opacity: ${({ isVisible }) => isVisible ? 1 : 0};
  pointer-events: ${({ isVisible }) => isVisible ? 'auto' : 'none'};
`;

const ReportProblemButton = styled(ButtonFloating)`
  z-index: 108; // TabBar is 109
  right: ${themeResponsiveProperty('1rem', '5rem')};
  // 4.65rem is about the TabBar height, 1rem is the padding
  bottom: ${themeResponsiveProperty(`calc(4.65rem + 1rem + env(safe-area-inset-bottom))`, 'max(1rem, env(safe-area-inset-bottom))')};
  display: flex;
  flex-direction: row;
  padding-right: ${themeResponsiveProperty('1rem', '1.5rem')};
  padding-left: ${themeResponsiveProperty('1rem', '1.5rem')};
  font-size: 1rem;
  color: ${themeResponsiveProperty('#fff', null)};
  background-color: ${themeResponsiveProperty('rgb(203 22 24 / .75);', '#ce2828')};
`;
